import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, FileBarChart, FileText, Loader2, Presentation, Save } from "lucide-react";
import { toast } from "sonner";
import { cloneDeep, set as lset } from "lodash";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AppHeader } from "@/components/AppHeader";
import { ClientSidebar } from "@/components/ClientSidebar";
import { KpiStrip } from "@/components/KpiStrip";
import { ClientFamilyTab } from "@/components/tabs/ClientFamilyTab";
import { FinancialTab } from "@/components/tabs/FinancialTab";
import { InvestRetireTab } from "@/components/tabs/InvestRetireTab";
import { InsuranceTab } from "@/components/tabs/InsuranceTab";
import { QuickStartTab } from "@/components/tabs/QuickStartTab";
import { api, productsApi } from "@/lib/api";
import { computeSynopsis } from "@/lib/calc";
import { migrate } from "@/lib/migrate";
import { requiredState } from "@/lib/required";
import { MissingBar } from "@/components/MissingBar";

const TABS = [["quick", "Quick Start"], ["client", "1 · Client & Family"], ["financial", "2 · Financial Overview"], ["investment", "3 · Investment & Retirement"], ["insurance", "4 · Insurance Needs"]];

export default function EditorPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [clients, setClients] = useState([]);
  const [data, setData] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState([]);
  const [tab, setTab] = useState("quick");

  useEffect(() => {
    setData(null);
    api.get(id).then((c) => { setData(migrate(c.data)); setDirty(false); }).catch(() => { toast.error("Client not found"); nav("/clients"); });
    api.list().then(setClients);
    productsApi.list().then(setProducts).catch(() => {});
  }, [id, nav]);

  const s = useMemo(() => (data ? computeSynopsis(data) : null), [data]);
  const req = useMemo(() => (s ? requiredState(s) : null), [s]);
  const edits = useRef(0);
  const set = (path, v) => { edits.current += 1; setData((d) => lset(cloneDeep(d), path, v)); setDirty(true); };

  const save = async (auto = false) => {
    const version = edits.current;
    setSaving(true);
    try {
      await api.update(id, data);
      if (edits.current === version) setDirty(false);
      if (!auto) toast.success("Client saved");
      api.list().then(setClients);
    } catch { toast.error("Save failed. Check your connection and try again."); }
    setSaving(false);
  };
  const go = async (path) => { if (dirty) await save(true); nav(path); };

  useEffect(() => {
    if (!dirty || saving) return undefined;
    const t = setTimeout(() => save(true), 1500);
    return () => clearTimeout(t);
  }, [data, dirty, saving]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const warn = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  if (!data) return <div className="min-h-screen grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-[#0072C6]" /></div>;
  const cur = data.profile.currency;
  const props = { d: data, s, set, cur, products, req };
  const jump = (m) => {
    setTab(m.tab);
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="${m.target}"]`);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      if (el.focus && el.tagName !== "SECTION") el.focus({ preventScroll: true });
    }, 80);
  };

  return (
    <div className="min-h-screen">
      <AppHeader>
        <Button variant="ghost" size="sm" onClick={() => nav("/clients")} data-testid="back-to-clients-button" className="gap-1.5"><ArrowLeft className="h-4 w-4" /> Clients</Button>
        <Button variant="outline" size="sm" onClick={() => go(`/clients/${id}/present`)} data-testid="meeting-mode-button" className="gap-1.5"><Presentation className="h-4 w-4" /> Meeting Mode</Button>
        <Button variant="outline" size="sm" onClick={() => go(`/clients/${id}/report`)} data-testid="button-print-report" className="gap-1.5"><FileText className="h-4 w-4" /> Detailed synopsis</Button>
        <Button variant="outline" size="sm" onClick={() => go(`/clients/${id}/fna`)} data-testid="generate-fna-report-button" className="gap-1.5 border-[#0072C6] text-[#0072C6]"><FileBarChart className="h-4 w-4" /> Generate FNA Report</Button>
        <Button size="sm" onClick={() => save()} disabled={saving} data-testid="save-client-button" className="gap-1.5">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {saving ? "Saving…" : "Save client"}
        </Button>
      </AppHeader>
      <div className="editor-grid">
        <ClientSidebar clients={clients} activeId={id} />
        <main className="min-w-0">
          <div className="sticky-top">
            <div className="flex items-baseline justify-between gap-3 mb-2">
              <h1 className="font-heading text-xl font-extrabold tracking-tight text-slate-900 truncate" data-testid="editor-client-name">{data.profile.name || "Untitled client"}</h1>
              <span className="text-xs whitespace-nowrap">{dirty ? <span className="text-amber-600 font-semibold" data-testid="unsaved-indicator">{saving ? "Saving…" : "Unsaved changes · auto-saving"}</span> : <span className="text-emerald-600 font-medium" data-testid="saved-indicator">✓ All changes saved</span>}</span>
            </div>
            <KpiStrip s={s} cur={cur} />
            <MissingBar list={req.list} onJump={jump} />
          </div>
          <Tabs value={tab} onValueChange={setTab} className="mt-3">
            <TabsList className="tab-list">
              {TABS.map(([k, label]) => {
                const count = req.list.filter((m) => m.tab === k).length;
                return <TabsTrigger key={k} value={k} data-testid={`tab-${k}`} className="tab-trigger">{label}{count > 0 && <span className="tab-badge" data-testid={`tab-${k}-missing-count`}>{count}</span>}</TabsTrigger>;
              })}
            </TabsList>
            <TabsContent value="quick" data-testid="section-quick"><QuickStartTab {...props} /></TabsContent>
            <TabsContent value="client" data-testid="section-client"><ClientFamilyTab {...props} /></TabsContent>
            <TabsContent value="financial" data-testid="section-financial"><FinancialTab {...props} /></TabsContent>
            <TabsContent value="investment" data-testid="section-investment"><InvestRetireTab {...props} /></TabsContent>
            <TabsContent value="insurance" data-testid="section-insurance"><InsuranceTab {...props} /></TabsContent>
          </Tabs>
        </main>
      </div>
    </div>
  );
}
